import { useCallback, useEffect, useState } from "react";
import { sampleBriefing } from "../data/sampleBriefing";
import type { UiArticle, UiBriefing, UiMetric, UiSourceProfile, UiStory } from "../types";

const CACHE_KEY = "newslens.briefing.v2";
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const METRIC_LABELS: Record<string, string> = {
  factDensity: "Fact density", emotionality: "Emotionality", headlineRhetoric: "Headline rhetoric",
  speculation: "Speculation", attributionQuality: "Attribution quality", sourceDiversity: "Source diversity",
  anonymousReliance: "Anonymous reliance", statisticalContext: "Statistical context",
};

export type BriefingSourceState = "loading" | "cache" | "live" | "demo" | "error";

function demoRequested(): boolean {
  return import.meta.env.VITE_ENABLE_DEMO === "true" || new URLSearchParams(window.location.search).get("demo") === "1";
}

function metricFromEngine(key: string, value: any): UiMetric {
  return {
    key, label: METRIC_LABELS[key] ?? key, value: value?.score ?? 0, rawMatches: value?.rawMatches ?? 0,
    weightedMatches: value?.weightedMatches ?? 0, denominator: value?.denominator ?? 0,
    normalization: value?.normalization ?? "Not supplied", applicable: value?.applicable ?? true,
  };
}

function sourceProfile(source: any): UiSourceProfile {
  return {
    id: String(source.id ?? "unknown"),
    name: String(source.name ?? source.id ?? "Unknown source"),
    type: String(source.type ?? "Configured publication"),
    country: String(source.country ?? ""),
    orientation: source.orientation ?? { label: null, source: "", methodology: "", lastReviewed: "" },
    ownership: String(source.ownership?.organization ?? source.ownership ?? "Not supplied"),
  };
}

function articleLabels(metrics: any, findings: any[]): string[] {
  const labels: string[] = [];
  if ((metrics.factDensity?.score ?? 0) >= 70) labels.push("HIGH FACT DENSITY");
  if ((metrics.emotionality?.score ?? 0) >= 55) labels.push("ELEVATED EMOTIONAL LANGUAGE");
  if ((metrics.speculation?.score ?? 0) >= 50) labels.push("ELEVATED SPECULATION");
  if ((metrics.sourceDiversity?.score ?? 100) <= 30) labels.push("LIMITED SOURCE DIVERSITY");
  if (findings.some((finding) => String(finding.type).startsWith("headline") || finding.type === "attribution_removed")) labels.push("HEADLINE AUDIT FINDING");
  return labels.slice(0, 3);
}

function articleFromEngine(article: any, sourceNames: Map<string, string>): UiArticle {
  const metrics = article.analysis?.metrics ?? {};
  const findings = article.analysis?.findings ?? [];
  const headlineFinding = findings.find((finding: any) => String(finding.type).startsWith("headline") || finding.type === "attribution_removed");
  const attributionFinding = findings.find((finding: any) => finding.type === "attribution_asymmetry");
  const loaded = article.analysis?.loadedTerms ?? [];
  return {
    id: article.id,
    publisherId: article.publisherId,
    publisherName: sourceNames.get(article.publisherId) ?? article.publisherId,
    headline: article.headline,
    url: article.url,
    author: article.author ?? "",
    publishedAt: article.publishedAt ?? "",
    section: article.section ?? "",
    wordCount: article.wordCount ?? 0,
    fullTextStatus: article.fullTextStatus ?? "unavailable",
    behaviorLabels: articleLabels(metrics, findings),
    profile: Object.keys(METRIC_LABELS).map((key) => metricFromEngine(key, metrics[key])),
    headlineAudit: headlineFinding ? { finding: headlineFinding.ruleDescription, severity: headlineFinding.severity, evidence: headlineFinding.evidence ?? [] } : { finding: "No material mismatch detected", severity: "none", evidence: [] },
    languageFinding: `${loaded.filter((term: any) => term.scope === "journalist").length} loaded terms in publisher-written text`,
    attributionFinding: attributionFinding ? "Attribution terminology differs after sample thresholds" : "No asymmetry detected or sample threshold not met",
    loadedTerms: loaded,
    versions: { engine: article.analysis?.engineVersion ?? "unknown", lexicon: article.analysis?.lexiconVersion ?? "unknown", rules: article.analysis?.rulesVersion ?? "unknown" },
    rawEvidence: {
      normalizedTokens: String(article.analysis?.normalizedText ?? "").split(/\s+/).filter(Boolean).slice(0, 80),
      sentenceScores: (article.analysis?.factCandidates ?? []).map((candidate: any) => ({ sentence: candidate.sentence, score: candidate.score })),
      ruleFirings: findings.map((finding: any) => finding.ruleId),
    },
  };
}

function storyStatus(story: any): UiStory["status"] {
  if ((story.disputedClaims?.length ?? 0) > 0) return "DISPUTED";
  if (story.status === "early reporting") return "EARLY REPORTING";
  if (story.status === "developing") return "DEVELOPING";
  if (story.confidence === "HIGH") return "HIGH CONSENSUS";
  if (story.confidence === "MEDIUM") return "MODERATE CONSENSUS";
  return "DEVELOPING";
}

export function adaptEngineBriefing(raw: any): UiBriefing | null {
  if (!Array.isArray(raw?.stories) || raw.stories.length === 0) return null;
  if (typeof raw.dateLabel === "string") return raw as UiBriefing;
  const suppliedSources: UiSourceProfile[] = Array.isArray(raw.sources) ? raw.sources.map(sourceProfile) : [];
  const sourceNames = new Map<string, string>(suppliedSources.map((source: UiSourceProfile) => [source.id, source.name]));
  const name = (publisherId: string) => sourceNames.get(publisherId) ?? publisherId;
  const stories: UiStory[] = raw.stories.map((story: any) => ({
    id: story.eventId,
    headline: story.headline,
    category: story.category || "Major stories",
    updatedLabel: String(story.status ?? "developing").replace(/\b\w/g, (letter) => letter.toUpperCase()),
    status: storyStatus(story),
    sourceCount: story.sourceCount,
    independentChainCount: story.independentChainCount,
    facts: (story.consensusFacts ?? []).map((fact: any) => ({ text: fact.text, consensus: fact.consensus, supportedBy: fact.sourceIds.map(name), independentChains: fact.independentChains, primaryConfirmation: fact.primarySourceUrl })),
    unclear: (story.attributedClaims ?? []).map((fact: any) => fact.text),
    disputed: (story.disputedClaims ?? []).map((item: any) => ({ field: item.field, variants: item.values.map((value: any) => ({ ...value, sources: value.sources.map(name) })) })),
    coverage: (story.coverageDifferences ?? []).map((item: any) => ({ publisherId: item.publisherId, publisherName: name(item.publisherId), summary: item.primaryFrame, emphasis: item.emphasis })),
    contrast: story.coverageContrast ? {
      publisherA: story.coverageContrast.publisherA,
      publisherB: story.coverageContrast.publisherB,
      publisherNameA: name(story.coverageContrast.publisherA),
      publisherNameB: name(story.coverageContrast.publisherB),
      score: story.coverageContrast.score,
      frameA: story.coverageContrast.frameA,
      frameB: story.coverageContrast.frameB,
      dimensions: (story.coverageContrast.dimensions ?? []).map((item: any) => ({ ...item, label: METRIC_LABELS[item.metric] ?? item.metric })),
    } : null,
    terminology: (story.terminologyDifferences ?? []).flatMap((item: any) => item.terms?.length >= 2 ? item.terms.slice(1).map((term: string) => ({ left: item.terms[0], right: term })) : []),
    primarySources: story.primarySources ?? [],
    articles: (story.articles ?? []).map((article: any) => articleFromEngine(article, sourceNames)),
  }));
  if (suppliedSources.length === 0) {
    for (const publisherId of new Set(stories.flatMap((story) => story.articles.map((article) => article.publisherId)))) {
      suppliedSources.push(sourceProfile({ id: publisherId, name: publisherId }));
    }
  }
  return {
    dateLabel: new Date(raw.generatedAt ?? Date.now()).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }),
    generatedAt: raw.generatedAt,
    isSample: false,
    pipeline: raw.pipeline,
    stories,
    sources: suppliedSources,
  };
}

function cachedBriefing(): UiBriefing | null {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) ?? "null");
    return adaptEngineBriefing(raw);
  } catch {
    return null;
  }
}

export function useBriefing() {
  const initialCache = cachedBriefing();
  const wantsDemo = demoRequested();
  const [briefing, setBriefing] = useState<UiBriefing | null>(() => initialCache ?? (wantsDemo ? sampleBriefing : null));
  const [source, setSource] = useState<BriefingSourceState>(() => initialCache ? "cache" : wantsDemo ? "demo" : "loading");
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (wantsDemo) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 60_000);
    if (!briefing) setSource("loading");
    setError(null);
    fetch(`${API_BASE}/api/briefing`, { signal: controller.signal, headers: { Accept: "application/json" } })
      .then(async (response) => {
        const raw = await response.json().catch(() => null);
        if (!response.ok) throw new Error(raw?.detail ?? `Briefing API returned HTTP ${response.status}`);
        return raw;
      })
      .then((raw) => {
        const next = adaptEngineBriefing(raw);
        if (!next) throw new Error("The briefing API returned no analyzable stories.");
        localStorage.setItem(CACHE_KEY, JSON.stringify(raw));
        setBriefing(next);
        setSource("live");
      })
      .catch((reason) => {
        if (controller.signal.aborted) setError("The live collection run took too long. Try again in a moment.");
        else setError(reason instanceof Error ? reason.message : String(reason));
        setSource(briefing ? "cache" : "error");
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt, wantsDemo]);

  return { briefing, source, error, retry };
}
